import React from "react";
import { motion } from "framer-motion";
import {
  Smartphone,
  CheckCircle,
  TrendingDown,
  Apple,
  Bot,
  Globe,
  GitBranch,
  Zap,
  Shield,
  ShieldCheck,
  Server,
  Lock,
  ShoppingCart,
  Package,
  CreditCard,
  Truck,
  Brain,
  Cpu,
  Sparkles,
  AlertCircle,
  Heart,
  Activity,
  Wallet,
  ArrowUp,
  ArrowDown,
  MapPin,
  Star,
  Home,
  Bath,
  Bed,
  Maximize2,
  BookOpen,
  Award,
  Flame,
  X,
  ScanLine,
  RotateCw,
  Plus,
  Search,
  Users,
  MessageSquare,
  FileText,
  Plane,
  Compass,
  Bell,
  Clock,
  Calculator,
  DollarSign,
  Timer,
  Rocket,
  Store,
  Layers,
} from "lucide-react";
import { FloatingCard, PhoneFrame } from "./HeroVisuals";

const sectionMockupBase = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8, delay: 0.2, ease: "easeOut" },
};

/**
 * Mid-page section mockup for the Mobile App Development service page.
 * Pairs with copy about delivery visibility: live builds, release pipeline,
 * crash-free sessions, and sprint velocity.
 */
export function MobileDeliveryDashboardVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="mobile-delivery-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#00AEEF]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  maxiom.app/projects/fittrack-pro
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {/* Project header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#00AEEF] flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">
                      FitTrack Pro
                    </div>
                    <div className="text-[9px] text-gray-500">
                      Sprint 14 · Build 2.4.0
                    </div>
                  </div>
                </div>
                <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
                  On track
                </div>
              </div>

              {/* KPI tiles */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Crash-Free", val: "99.94%", pct: "+0.3%", color: "#10B981" },
                  { label: "Coverage", val: "92%", pct: "+4%", color: "#00AEEF" },
                  { label: "Rating", val: "4.8", pct: "+0.1", color: "#F59E0B" },
                ].map((m, i) => (
                  <div
                    key={i}
                    className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5"
                  >
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-1">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>
                      {m.pct}
                    </div>
                  </div>
                ))}
              </div>

              {/* Release pipeline */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Release Pipeline</div>
                  <div className="text-[8px] text-gray-600">v2.4.0</div>
                </div>
                <div className="space-y-2">
                  {[
                    {
                      icon: Apple,
                      label: "iOS Build #248 → TestFlight",
                      status: "Shipped",
                      color: "#10B981",
                    },
                    {
                      icon: Bot,
                      label: "Android Build #214 → Internal",
                      status: "Shipped",
                      color: "#10B981",
                    },
                    {
                      icon: Apple,
                      label: "iOS 2.4.0 → App Store",
                      status: "In Review",
                      color: "#F59E0B",
                    },
                  ].map((r, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + i * 0.15, duration: 0.4 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <r.icon className="w-3 h-3 text-gray-500" />
                        <span className="text-[9px] text-gray-300">{r.label}</span>
                      </div>
                      <span
                        className="text-[8px] px-1.5 py-0.5 rounded"
                        style={{
                          color: r.color,
                          backgroundColor: `${r.color}15`,
                        }}
                      >
                        {r.status}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Sprint velocity bar chart */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Sprint Velocity</div>
                  <div className="text-[9px] text-[#00AEEF] font-medium">
                    42 pts · +12%
                  </div>
                </div>
                <div className="flex items-end gap-1 h-14">
                  {[28, 35, 30, 38, 33, 40, 37, 42].map((h, i) => {
                    const isLast = i === 7;
                    return (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${(h / 42) * 100}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + i * 0.08, duration: 0.5 }}
                        className={`flex-1 rounded-sm ${
                          isLast ? "bg-[#00AEEF]" : "bg-[#00AEEF]/30"
                        }`}
                      />
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[8px] text-gray-600">S7</span>
                  <span className="text-[8px] text-gray-600">S14</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">App Store</div>
            <div className="text-[#10B981] text-[8px]">Approved 2h ago</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <TrendingDown className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Crashes</div>
            <div className="text-gray-400 text-[8px]">−68% vs last release</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Support and maintenance: the desk after launch.
 * Store updates, crash triage, OS bumps, and a named person on call.
 * Accent: #00AEEF.
 */
export function AppSupportDeskVisual() {
  const queue = [
    { label: "iOS 18.2 privacy label", status: "Ready", color: "#10B981" },
    { label: "Sign-in crash, iPhone 15", status: "Triage", color: "#F59E0B" },
    { label: "Push certificate", status: "Friday", color: "#00AEEF" },
    { label: "Maps SDK 2.4.1", status: "Queued", color: "#A3A3A3" },
  ];

  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="app-support-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#00AEEF]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">support.maxiom.app/northline</span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#00AEEF] flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">Northline Field</div>
                    <div className="text-[9px] text-gray-500">Build 4.2.1 · On call Maya Chen</div>
                  </div>
                </div>
                <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
                  Live
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Crash-free", val: "99.4%", note: "this week", color: "#10B981" },
                  { label: "iOS 18.2", val: "Ready", note: "store update", color: "#00AEEF" },
                  { label: "Push cert", val: "18 days", note: "on the calendar", color: "#F59E0B" },
                ].map((m) => (
                  <div key={m.label} className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5">
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-1">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>
                      {m.note}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">This week</div>
                  <div className="text-[8px] text-gray-600">4 open</div>
                </div>
                <div className="space-y-2">
                  {queue.map((row, i) => (
                    <motion.div
                      key={row.label}
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.45 + i * 0.12, duration: 0.4 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Bell className="w-3 h-3 text-gray-500" />
                        <span className="text-[9px] text-gray-300">{row.label}</span>
                      </div>
                      <span
                        className="text-[8px] px-1.5 py-0.5 rounded"
                        style={{ color: row.color, backgroundColor: `${row.color}15` }}
                      >
                        {row.status}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Crash-free sessions</div>
                  <div className="text-[9px] text-[#10B981] font-medium">99.4%</div>
                </div>
                <div className="flex items-end gap-1 h-12">
                  {[72, 80, 76, 88, 84, 91, 86, 94].map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.35 + i * 0.06, duration: 0.45 }}
                      className={`flex-1 rounded-sm ${i === 7 ? "bg-[#00AEEF]" : "bg-[#00AEEF]/30"}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Store update</div>
            <div className="text-[#10B981] text-[8px]">Submitted 2h ago</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Clock className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">On call</div>
            <div className="text-gray-400 text-[8px]">Maya Chen · same day</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Web App Development: deploy pipeline + Core Web Vitals dashboard.
 * Accent: #10B981 (green).
 */
export function WebDeploymentDashboardVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="web-delivery-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#10B981]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  cloud.maxiom.app/web/acme
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#10B981] flex items-center justify-center">
                    <Globe className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">Acme Web</div>
                    <div className="text-[9px] text-gray-500">main · 2 mins ago</div>
                  </div>
                </div>
                <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
                  Production
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Lighthouse", val: "98", pct: "+3", color: "#10B981" },
                  { label: "LCP", val: "1.2s", pct: "−0.4s", color: "#00AEEF" },
                  { label: "Uptime", val: "99.99%", pct: "30d", color: "#8B5CF6" },
                ].map((m, i) => (
                  <div
                    key={i}
                    className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5"
                  >
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-1">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>
                      {m.pct}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Recent Deploys</div>
                  <div className="text-[8px] text-gray-600">47s avg</div>
                </div>
                <div className="space-y-2">
                  {[
                    { label: "main → production", status: "Deployed", color: "#10B981" },
                    { label: "main → preview", status: "Deployed", color: "#10B981" },
                    { label: "pr/auth-flow → preview", status: "Building", color: "#F59E0B" },
                  ].map((r, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + i * 0.15, duration: 0.4 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <GitBranch className="w-3 h-3 text-gray-500" />
                        <span className="text-[9px] text-gray-300">{r.label}</span>
                      </div>
                      <span
                        className="text-[8px] px-1.5 py-0.5 rounded"
                        style={{
                          color: r.color,
                          backgroundColor: `${r.color}15`,
                        }}
                      >
                        {r.status}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">
                    Core Web Vitals · 24h
                  </div>
                  <div className="text-[9px] text-[#10B981] font-medium">All Green</div>
                </div>
                <div className="flex items-end gap-1 h-12">
                  {[60, 65, 70, 68, 75, 80, 78, 85, 82, 88, 90, 92].map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.05, duration: 0.4 }}
                      className="flex-1 rounded-sm bg-gradient-to-t from-[#10B981] to-[#10B981]/30"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">0 Errors</div>
            <div className="text-[#10B981] text-[8px]">Past 24h</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="font-mono text-[10px]">
          <span className="text-[#8B5CF6]">deploy</span>{" "}
          <span className="text-[#10B981]">main</span>{" "}
          <span className="text-gray-400">--prod</span>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Custom Software: production health, service map, audit log.
 * Accent: #8B5CF6 (purple).
 */
export function PlatformOpsDashboardVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="platform-ops-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#8B5CF6]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  platform.enterprise.io/ops
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#8B5CF6] flex items-center justify-center">
                    <Server className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">
                      Production Health
                    </div>
                    <div className="text-[9px] text-gray-500">
                      12 services · us-east-1
                    </div>
                  </div>
                </div>
                <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
                  Healthy
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "SLA · 30d", val: "99.99%", pct: "+0.01%", color: "#10B981" },
                  { label: "Error Budget", val: "87%", pct: "remaining", color: "#8B5CF6" },
                  { label: "MTTR", val: "12m", pct: "−4m", color: "#00AEEF" },
                ].map((m, i) => (
                  <div
                    key={i}
                    className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5"
                  >
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-1">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>
                      {m.pct}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="text-[9px] text-gray-500 mb-2">Service Map</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { name: "Auth", color: "#10B981" },
                    { name: "API Gateway", color: "#10B981" },
                    { name: "Users", color: "#10B981" },
                    { name: "Payments", color: "#10B981" },
                    { name: "Search", color: "#F59E0B" },
                    { name: "Notifications", color: "#10B981" },
                  ].map((s, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.85 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.08, duration: 0.35 }}
                      className="bg-[#0f0f0f] rounded-md px-2 py-1.5 flex items-center gap-1.5 border border-white/5"
                    >
                      <div
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="text-[8px] text-gray-300 truncate">
                        {s.name}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Audit Log</div>
                  <div className="text-[8px] text-gray-600">SOC 2 trail</div>
                </div>
                <div className="space-y-1.5">
                  {[
                    {
                      actor: "j.chen",
                      action: "Role updated · admin → viewer",
                      time: "2m",
                    },
                    {
                      actor: "ci-bot",
                      action: "Policy applied · v2.4.1",
                      time: "14m",
                    },
                    {
                      actor: "m.silva",
                      action: "Deploy approved · payments",
                      time: "1h",
                    },
                  ].map((l, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6 + i * 0.15 }}
                      className="flex items-center justify-between text-[9px]"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[#8B5CF6] font-mono">{l.actor}</span>
                        <span className="text-gray-400 truncate">{l.action}</span>
                      </div>
                      <span className="text-gray-600 flex-shrink-0">{l.time}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#8B5CF6]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">SOC 2 Type II</div>
            <div className="text-[#10B981] text-[8px]">0 findings</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Lock className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Encryption</div>
            <div className="text-gray-400 text-[8px]">At rest · In transit</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * E-commerce: merchant ops dashboard with revenue, orders pipeline, hourly chart.
 * Accent: #F59E0B (orange).
 */
export function CommerceOpsDashboardVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="commerce-ops-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#F59E0B]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  merchant.luxestore.app/dashboard
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#F59E0B] flex items-center justify-center">
                    <ShoppingCart className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">LuxeStore</div>
                    <div className="text-[9px] text-gray-500">Today · Live</div>
                  </div>
                </div>
                <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
                  +34% DoD
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Revenue", val: "$48,219", pct: "+34%", color: "#10B981" },
                  { label: "Conv.", val: "3.8%", pct: "+0.6pp", color: "#F59E0B" },
                  { label: "AOV", val: "$186", pct: "+$12", color: "#00AEEF" },
                ].map((m, i) => (
                  <div
                    key={i}
                    className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5"
                  >
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-1">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>
                      {m.pct}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Orders Pipeline</div>
                  <div className="text-[8px] text-gray-600">Today</div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: "Pending", val: 12, icon: AlertCircle, color: "#F59E0B" },
                    { label: "Picking", val: 8, icon: Package, color: "#00AEEF" },
                    { label: "Shipped", val: 42, icon: Truck, color: "#8B5CF6" },
                    { label: "Delivered", val: 38, icon: CheckCircle, color: "#10B981" },
                  ].map((s, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.12, duration: 0.35 }}
                      className="bg-[#0f0f0f] rounded-md p-2 border border-white/5 text-center"
                    >
                      <s.icon
                        className="w-3 h-3 mx-auto mb-1"
                        style={{ color: s.color }}
                      />
                      <div className="text-white text-xs font-bold">{s.val}</div>
                      <div className="text-[7px] text-gray-500">{s.label}</div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Revenue · Hourly</div>
                  <div className="text-[9px] text-[#F59E0B] font-medium">Peak 2pm</div>
                </div>
                <div className="flex items-end gap-1 h-12">
                  {[30, 35, 45, 55, 65, 75, 85, 95, 88, 75, 60, 50].map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.05, duration: 0.4 }}
                      className="flex-1 rounded-sm bg-gradient-to-t from-[#F59E0B] to-[#F59E0B]/30"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Recovered cart</div>
            <div className="text-[#10B981] text-[8px]">+$349.00</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
            <Shield className="w-4 h-4 text-[#EC4899]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Fraud Blocked</div>
            <div className="text-gray-400 text-[8px]">3 attempts today</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * AI/ML: model observability with KPIs, training runs, drift sparkline.
 * Accent: #EC4899 (pink).
 */
export function ModelOpsDashboardVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="model-ops-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#EC4899]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  ml.platform.io/models/forecast-v3
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#EC4899] flex items-center justify-center">
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">Forecast v3</div>
                    <div className="text-[9px] text-gray-500">
                      Production · 1.2M req/day
                    </div>
                  </div>
                </div>
                <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
                  Stable
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Accuracy", val: "97.4%", pct: "+0.3pp", color: "#EC4899" },
                  { label: "P95 Latency", val: "86ms", pct: "−12ms", color: "#10B981" },
                  { label: "Drift", val: "0.04", pct: "stable", color: "#00AEEF" },
                ].map((m, i) => (
                  <div
                    key={i}
                    className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5"
                  >
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-1">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>
                      {m.pct}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Recent Runs</div>
                  <div className="text-[8px] text-gray-600">A/B active</div>
                </div>
                <div className="space-y-2">
                  {[
                    { label: "run-217 · 240 epochs", status: "97.4%", color: "#10B981" },
                    { label: "run-218 · 280 epochs", status: "evaluating", color: "#F59E0B" },
                    { label: "A/B v3.1 vs v3.0", status: "+2.1pp", color: "#EC4899" },
                  ].map((r, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + i * 0.15, duration: 0.4 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-gray-500" />
                        <span className="text-[9px] text-gray-300">{r.label}</span>
                      </div>
                      <span
                        className="text-[8px] px-1.5 py-0.5 rounded font-mono"
                        style={{
                          color: r.color,
                          backgroundColor: `${r.color}15`,
                        }}
                      >
                        {r.status}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Input Drift · 14d</div>
                  <div className="text-[9px] text-[#10B981] font-medium">No alerts</div>
                </div>
                <div className="flex items-end gap-1 h-12">
                  {[20, 25, 22, 28, 24, 30, 26, 28, 22, 25, 20, 24, 22, 26].map(
                    (h, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${h}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + i * 0.04, duration: 0.4 }}
                        className="flex-1 rounded-sm bg-gradient-to-t from-[#EC4899] to-[#EC4899]/30"
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
            <Cpu className="w-4 h-4 text-[#EC4899]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">GPU Util</div>
            <div className="text-[#EC4899] text-[8px]">78% · 4 nodes</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="font-mono text-[10px]">
          <span className="text-[#EC4899]">model</span>
          <span className="text-white">.</span>
          <span className="text-[#10B981]">deploy</span>
          <span className="text-gray-400">(v3.1)</span>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/* ------------------------------------------------------------------------ *
 * Landing-page contextual visuals: industry-themed phone mockups paired
 * with showcase copy. Reused across multiple landing pages by slug map.
 * ------------------------------------------------------------------------ */

/** Healthcare / Telehealth: patient portal with vitals, appointment, adherence. */
export function HealthcareAppVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="healthcare-visual"
    >
      <PhoneFrame accentColor="#10B981">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] text-gray-500">Good morning</div>
              <div className="text-white text-sm font-bold">Jordan</div>
            </div>
            <div className="w-9 h-9 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
              <Heart className="w-4 h-4 text-[#10B981]" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "BP", val: "118/76", color: "#10B981" },
              { label: "HR", val: "68", color: "#EC4899" },
              { label: "SpO₂", val: "98%", color: "#00AEEF" },
            ].map((v, i) => (
              <div
                key={i}
                className="bg-[#1a1a1a] rounded-xl p-2.5 border border-white/5 text-center"
              >
                <div className="text-[8px] text-gray-500">{v.label}</div>
                <div
                  className="text-sm font-bold mt-1"
                  style={{ color: v.color }}
                >
                  {v.val}
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#10B981]/10 border border-[#10B981]/20 rounded-xl p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[9px] text-[#10B981] font-medium">
                Upcoming · Telehealth
              </span>
              <span className="text-[9px] text-gray-400">Tue 10:30 AM</span>
            </div>
            <div className="text-white text-xs font-semibold">Dr. Anita Patel</div>
            <div className="text-[10px] text-gray-400 mt-0.5">
              Annual physical follow-up
            </div>
            <div className="h-6 bg-[#10B981] rounded-md mt-2 flex items-center justify-center">
              <span className="text-[9px] text-white font-semibold">
                Join Video Visit
              </span>
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] text-gray-500">Med Adherence · 7d</span>
              <span className="text-[9px] text-[#10B981]">100%</span>
            </div>
            <div className="flex items-end gap-1.5 h-8">
              {[1, 1, 1, 1, 1, 1, 1].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  whileInView={{ height: "100%" }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.08, duration: 0.4 }}
                  className="flex-1 rounded-sm bg-[#10B981]/60"
                />
              ))}
            </div>
            <div className="flex justify-between mt-1 text-[7px] text-gray-600">
              {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">HIPAA compliant</div>
            <div className="text-gray-400 text-[8px]">SOC 2 Type II</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Activity className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">EHR-synced</div>
            <div className="text-gray-400 text-[8px]">Epic · Cerner · Athena</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** Fintech / Crypto: portfolio wallet with assets list and sparkline. */
export function FintechAppVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="fintech-visual"
    >
      <PhoneFrame accentColor="#F59E0B">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] text-gray-500">Total Portfolio</div>
              <div className="text-white text-lg font-bold">$47,892.34</div>
              <div className="text-[#10B981] text-[10px]">+$1,924.18 · +4.2%</div>
            </div>
            <div className="w-9 h-9 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-[#F59E0B]" />
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5">
            <div className="flex items-end gap-1 h-14">
              {[40, 55, 45, 65, 55, 75, 65, 85, 70, 88, 80, 95].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  whileInView={{ height: `${h}%` }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.05, duration: 0.4 }}
                  className="flex-1 rounded-sm bg-gradient-to-t from-[#F59E0B] to-[#F59E0B]/20"
                />
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            {[
              { sym: "BTC", name: "Bitcoin", val: "$24,180.00", pct: "+3.4%", up: true },
              { sym: "ETH", name: "Ethereum", val: "$11,420.12", pct: "+5.1%", up: true },
              { sym: "AAPL", name: "Apple", val: "$9,892.22", pct: "−0.8%", up: false },
              { sym: "TSLA", name: "Tesla", val: "$2,400.00", pct: "+1.2%", up: true },
            ].map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 + i * 0.1 }}
                className="bg-[#1a1a1a] rounded-xl p-2 border border-white/5 flex items-center gap-2"
              >
                <div className="w-7 h-7 rounded-full bg-[#F59E0B]/15 flex items-center justify-center">
                  <span className="text-[#F59E0B] text-[9px] font-bold">{a.sym}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-[10px] font-medium truncate">
                    {a.name}
                  </div>
                  <div className="text-gray-500 text-[8px]">{a.val}</div>
                </div>
                <div
                  className="text-[9px] font-mono flex items-center gap-0.5"
                  style={{ color: a.up ? "#10B981" : "#EC4899" }}
                >
                  {a.up ? (
                    <ArrowUp className="w-2.5 h-2.5" />
                  ) : (
                    <ArrowDown className="w-2.5 h-2.5" />
                  )}
                  {a.pct}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
            <Lock className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">PCI-DSS</div>
            <div className="text-gray-400 text-[8px]">Tokenized · KYC ready</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Plaid linked</div>
            <div className="text-gray-400 text-[8px]">Stripe · Solana · Circle</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** On-demand / Food delivery: customer order tracking with route and driver card. */
export function OnDemandAppVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="ondemand-visual"
    >
      <PhoneFrame accentColor="#F59E0B">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] text-gray-500">Order #4827</div>
              <div className="text-white text-sm font-bold">Arriving in 12 min</div>
            </div>
            <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
              En route
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-3 border border-white/5 relative h-24 overflow-hidden">
            <svg viewBox="0 0 200 80" className="absolute inset-0 w-full h-full opacity-40">
              <path
                d="M15,65 Q70,40 110,55 T185,20"
                stroke="#F59E0B"
                strokeWidth="2"
                strokeDasharray="3 3"
                fill="none"
              />
              <circle cx="15" cy="65" r="3" fill="#10B981" />
              <circle cx="185" cy="20" r="4" fill="#F59E0B">
                <animate
                  attributeName="cx"
                  values="110;120;130"
                  dur="3s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
            <div className="absolute bottom-2 left-3 text-[8px] text-gray-400">
              2.3 mi · 234 Maple St
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#EC4899]" />
            <div className="flex-1 min-w-0">
              <div className="text-white text-[11px] font-semibold">Marcus R.</div>
              <div className="text-gray-500 text-[9px]">Honda Civic · ABC-7421</div>
            </div>
            <div className="flex items-center gap-0.5 text-[#F59E0B]">
              <Star className="w-3 h-3 fill-current" />
              <span className="text-[10px] font-medium text-white">4.9</span>
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5">
            <div className="text-[9px] text-gray-500 mb-2">Your order</div>
            <div className="space-y-1.5">
              {[
                { name: "Margherita Pizza", qty: "1×" },
                { name: "Caesar Salad", qty: "2×" },
                { name: "Sparkling Water", qty: "2×" },
              ].map((it, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-[10px]"
                >
                  <span className="text-gray-300">
                    <span className="text-[#F59E0B] mr-2">{it.qty}</span>
                    {it.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
            <MapPin className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Live ETA</div>
            <div className="text-gray-400 text-[8px]">Mapbox · Google Maps</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Stripe Connect</div>
            <div className="text-gray-400 text-[8px]">Driver payouts · tips</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** Real Estate: property listing card with photo placeholder and specs. */
export function RealEstateAppVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="realestate-visual"
    >
      <PhoneFrame accentColor="#8B5CF6">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] text-gray-500">Featured listing</div>
              <div className="text-white text-sm font-bold">Cedar Heights</div>
            </div>
            <div className="w-9 h-9 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center">
              <Home className="w-4 h-4 text-[#8B5CF6]" />
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl border border-white/5 overflow-hidden">
            <div className="h-28 bg-gradient-to-br from-[#8B5CF6]/30 via-[#00AEEF]/15 to-[#0f0f0f] relative">
              <div className="absolute top-2 left-2 text-[8px] text-white/80 bg-black/40 px-1.5 py-0.5 rounded">
                For Sale
              </div>
              <div className="absolute bottom-2 right-2 text-[8px] text-white/80 bg-black/40 px-1.5 py-0.5 rounded flex items-center gap-1">
                <ScanLine className="w-2.5 h-2.5" />
                AR Tour
              </div>
            </div>
            <div className="p-3 space-y-1.5">
              <div className="flex items-baseline justify-between">
                <div className="text-white text-base font-bold">$789,000</div>
                <div className="text-[#10B981] text-[9px]">−$11K · 2d ago</div>
              </div>
              <div className="text-gray-400 text-[10px]">
                412 Cedar Ridge Dr · Bethesda, MD
              </div>
              <div className="flex items-center gap-3 pt-1 text-[10px]">
                {[
                  { icon: Bed, val: "3" },
                  { icon: Bath, val: "2.5" },
                  { icon: Maximize2, val: "1,840" },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-1 text-gray-300">
                    <s.icon className="w-3 h-3 text-[#8B5CF6]" />
                    <span>{s.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button className="h-9 bg-[#8B5CF6] rounded-xl flex items-center justify-center">
              <span className="text-white text-[10px] font-semibold">
                Schedule Tour
              </span>
            </button>
            <button className="h-9 bg-[#1a1a1a] border border-white/10 rounded-xl flex items-center justify-center">
              <span className="text-gray-300 text-[10px] font-medium">Save</span>
            </button>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-2.5 border border-white/5">
            <div className="text-[9px] text-gray-500 mb-1">Mortgage estimate</div>
            <div className="flex items-baseline justify-between">
              <div className="text-white text-xs font-bold">$4,212/mo</div>
              <div className="text-gray-500 text-[9px]">30y · 6.4%</div>
            </div>
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
            <Activity className="w-4 h-4 text-[#8B5CF6]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">MLS-synced</div>
            <div className="text-gray-400 text-[8px]">RESO · Bridge · Spark</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <ScanLine className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">AR walkthroughs</div>
            <div className="text-gray-400 text-[8px]">Matterport · 3D scans</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** Education / EdTech: course progress + lesson list + streak. */
export function EducationAppVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="education-visual"
    >
      <PhoneFrame accentColor="#00AEEF">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] text-gray-500">Currently learning</div>
              <div className="text-white text-sm font-bold">CS Foundations</div>
            </div>
            <div className="w-9 h-9 rounded-xl bg-[#00AEEF]/20 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-[#00AEEF]" />
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[9px] text-gray-500">Course progress</div>
              <div className="text-white text-xs font-bold">73%</div>
            </div>
            <div className="h-2 bg-[#0f0f0f] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: "73%" }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 1, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-[#00AEEF] to-[#8B5CF6]"
              />
            </div>
            <div className="text-[9px] text-gray-500 mt-2">11 of 15 lessons</div>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5 space-y-2">
            <div className="text-[9px] text-gray-500 mb-1">Up next</div>
            {[
              { num: "12", title: "Recursion fundamentals", time: "24 min", done: false },
              { num: "11", title: "Stacks & queues", time: "18 min", done: true },
              { num: "10", title: "Linked lists", time: "22 min", done: true },
            ].map((l, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + i * 0.12 }}
                className="flex items-center gap-2.5"
              >
                <div
                  className={`w-6 h-6 rounded-md flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${
                    l.done
                      ? "bg-[#10B981]/20 text-[#10B981]"
                      : "bg-[#00AEEF]/20 text-[#00AEEF]"
                  }`}
                >
                  {l.done ? <CheckCircle className="w-3 h-3" /> : l.num}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-[10px] truncate ${
                      l.done ? "text-gray-500 line-through" : "text-white font-medium"
                    }`}
                  >
                    {l.title}
                  </div>
                  <div className="text-[8px] text-gray-600">{l.time}</div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
              <Flame className="w-4 h-4 text-[#F59E0B]" />
            </div>
            <div className="flex-1">
              <div className="text-white text-[11px] font-semibold">12-day streak</div>
              <div className="text-gray-500 text-[9px]">Keep it going!</div>
            </div>
            <div className="text-[#F59E0B] text-base font-bold">🔥</div>
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Award className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Certificates</div>
            <div className="text-gray-400 text-[8px]">SCORM · xAPI · LTI</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Live cohorts</div>
            <div className="text-gray-400 text-[8px]">Zoom · Whiteboard · Q&amp;A</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** Dating: profile card + match strip + action buttons. */
export function DatingAppVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="dating-visual"
    >
      <PhoneFrame accentColor="#EC4899">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {["#EC4899", "#8B5CF6", "#00AEEF", "#F59E0B", "#10B981"].map((c, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.6 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="w-6 h-6 rounded-full border-2 border-[#1a1a1a]"
                  style={{
                    background: `linear-gradient(135deg, ${c}, ${c}80)`,
                    marginLeft: i === 0 ? 0 : -8,
                  }}
                />
              ))}
            </div>
            <div className="text-[8px] text-[#EC4899] bg-[#EC4899]/10 border border-[#EC4899]/20 px-2 py-1 rounded-full">
              5 new matches
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl border border-white/5 overflow-hidden">
            <div className="h-36 bg-gradient-to-br from-[#EC4899]/40 via-[#8B5CF6]/20 to-[#0f0f0f] relative">
              <div className="absolute top-2 right-2 text-[8px] text-white/80 bg-black/40 px-1.5 py-0.5 rounded flex items-center gap-1">
                <CheckCircle className="w-2.5 h-2.5 text-[#10B981]" />
                Verified
              </div>
            </div>
            <div className="p-3 space-y-1.5">
              <div className="flex items-baseline justify-between">
                <div className="text-white text-sm font-bold">Avery, 28</div>
                <div className="text-gray-500 text-[9px]">2 mi away</div>
              </div>
              <div className="text-gray-400 text-[10px] leading-relaxed">
                Designer, climber, perpetual coffee experimenter.
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {["Climbing", "Coffee ☕", "Design"].map((t, i) => (
                  <span
                    key={i}
                    className="text-[8px] text-[#EC4899] bg-[#EC4899]/10 border border-[#EC4899]/20 px-1.5 py-0.5 rounded-full"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4">
            {[
              { icon: X, color: "#EF4444", size: "w-10 h-10" },
              { icon: Star, color: "#00AEEF", size: "w-9 h-9" },
              { icon: Heart, color: "#EC4899", size: "w-10 h-10" },
            ].map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 + i * 0.1 }}
                className={`${b.size} rounded-full bg-[#1a1a1a] border border-white/10 flex items-center justify-center`}
              >
                <b.icon className="w-4 h-4" style={{ color: b.color }} />
              </motion.div>
            ))}
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Verified profiles</div>
            <div className="text-gray-400 text-[8px]">Photo + ID checks</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#EC4899]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Safer messaging</div>
            <div className="text-gray-400 text-[8px]">Block + report + report</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** AR / VR: phone with camera viewfinder, place-object UI, and asset library. */
export function ArVrAppVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="arvr-visual"
    >
      <PhoneFrame accentColor="#8B5CF6">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] text-gray-500">AR Scene</div>
              <div className="text-white text-sm font-bold">Living Room · Demo</div>
            </div>
            <div className="text-[8px] text-[#8B5CF6] bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 px-2 py-1 rounded-full">
              Anchored
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#8B5CF6]/20 via-[#00AEEF]/10 to-[#0f0f0f] rounded-2xl border border-white/5 h-40 relative overflow-hidden">
            <svg viewBox="0 0 200 140" className="absolute inset-0 w-full h-full">
              <line x1="0" y1="100" x2="200" y2="100" stroke="#ffffff20" strokeWidth="1" />
              <line x1="60" y1="0" x2="60" y2="140" stroke="#ffffff10" strokeWidth="1" />
              <line x1="140" y1="0" x2="140" y2="140" stroke="#ffffff10" strokeWidth="1" />
            </svg>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            >
              <div className="w-16 h-12 bg-gradient-to-br from-[#8B5CF6] to-[#8B5CF6]/40 rounded-md shadow-2xl border border-white/10" />
              <div className="w-20 h-1.5 bg-black/30 rounded-full mx-auto mt-1 blur-sm" />
            </motion.div>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 6, ease: "linear" }}
                className="w-24 h-24 border border-[#8B5CF6]/40 rounded-full"
                style={{ borderStyle: "dashed" }}
              />
            </div>
            <div className="absolute bottom-2 left-2 text-[8px] text-white/80 bg-black/40 px-1.5 py-0.5 rounded flex items-center gap-1">
              <ScanLine className="w-2.5 h-2.5" />
              Surface detected
            </div>
            <div className="absolute bottom-2 right-2 text-[8px] text-white/80 bg-black/40 px-1.5 py-0.5 rounded flex items-center gap-1">
              <RotateCw className="w-2.5 h-2.5" />
              45°
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-2 border border-white/5">
            <div className="text-[9px] text-gray-500 mb-2 px-1">Object library</div>
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { label: "Chair", color: "#8B5CF6", selected: true },
                { label: "Lamp", color: "#F59E0B" },
                { label: "Plant", color: "#10B981" },
                { label: "Art", color: "#EC4899" },
              ].map((o, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7 + i * 0.08 }}
                  className={`bg-[#0f0f0f] rounded-md p-1.5 text-center border ${
                    o.selected
                      ? "border-[#8B5CF6]/50"
                      : "border-white/5"
                  }`}
                >
                  <div
                    className="w-5 h-5 rounded mx-auto mb-1"
                    style={{ background: `${o.color}40`, border: `1px solid ${o.color}` }}
                  />
                  <div className="text-[8px] text-gray-300">{o.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
          <button className="w-full h-8 bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] rounded-md flex items-center justify-center gap-1.5">
            <Plus className="w-3 h-3 text-white" />
            <span className="text-[10px] text-white font-semibold">Place object</span>
          </button>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
            <ScanLine className="w-4 h-4 text-[#8B5CF6]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">ARKit · ARCore</div>
            <div className="text-gray-400 text-[8px]">LiDAR · plane detection</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#EC4899]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Unity · Unreal</div>
            <div className="text-gray-400 text-[8px]">WebXR · Meta SDK</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Generative AI: chat thread with model picker, streaming tokens, and a
 * RAG citations card. Browser chrome framing. Accent: #EC4899.
 */
export function GenAiChatVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="gen-ai-chat-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#EC4899]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  copilot.maxiom.app/threads/onboarding
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {/* Model picker row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#EC4899] flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">
                      Onboarding Copilot
                    </div>
                    <div className="text-[9px] text-gray-500">
                      RAG · 12,418 documents indexed
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-[#1a1a1a] border border-white/5 rounded-full px-2 py-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899]" />
                  <span className="text-[9px] text-gray-300 font-mono">
                    claude-sonnet-4
                  </span>
                </div>
              </div>

              {/* User message */}
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex justify-end"
              >
                <div className="bg-[#EC4899]/15 border border-[#EC4899]/30 rounded-xl rounded-tr-sm px-3 py-2 max-w-[80%]">
                  <div className="text-[10px] text-gray-200">
                    How do I configure SSO for a new enterprise tenant?
                  </div>
                </div>
              </motion.div>

              {/* Assistant streaming */}
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 }}
                className="flex gap-2"
              >
                <div className="w-6 h-6 rounded-lg bg-[#EC4899]/20 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3 h-3 text-[#EC4899]" />
                </div>
                <div className="flex-1 bg-[#1a1a1a] border border-white/5 rounded-xl rounded-tl-sm px-3 py-2">
                  <div className="text-[10px] text-gray-200 leading-relaxed">
                    For an enterprise tenant, enable SAML in{" "}
                    <span className="text-[#EC4899] font-mono">
                      Admin → Identity
                    </span>
                    , upload the IdP metadata XML, then map group claims to
                    Maxiom roles
                    <motion.span
                      animate={{ opacity: [0.2, 1, 0.2] }}
                      transition={{ duration: 1.2, repeat: Infinity }}
                      className="inline-block w-1.5 h-3 bg-[#EC4899] ml-0.5 align-middle"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/5">
                    <FileText className="w-2.5 h-2.5 text-[#EC4899]" />
                    <span className="text-[8px] text-gray-500">
                      3 sources cited
                    </span>
                    <div className="flex gap-1 ml-auto">
                      {["okta", "saml", "rbac"].map((t) => (
                        <span
                          key={t}
                          className="text-[8px] text-gray-400 bg-[#0f0f0f] border border-white/5 rounded px-1.5 py-0.5 font-mono"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Eval / cost telemetry */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Latency", val: "412ms", color: "#10B981" },
                  { label: "Eval score", val: "94.2", color: "#EC4899" },
                  { label: "Cost / req", val: "$0.018", color: "#00AEEF" },
                ].map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1.0 + i * 0.1 }}
                    className="bg-[#1a1a1a] rounded-lg p-2 border border-white/5"
                  >
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div
                      className="text-sm font-bold mt-0.5"
                      style={{ color: m.color }}
                    >
                      {m.val}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
            <Brain className="w-4 h-4 text-[#EC4899]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">
              RAG indexed
            </div>
            <div className="text-gray-400 text-[8px]">
              12,418 docs · pgvector
            </div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">
              Eval suite
            </div>
            <div className="text-gray-400 text-[8px]">
              42 regression tests
            </div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Travel: phone frame with flight itinerary card, boarding pass strip,
 * trip timeline, and a loyalty miles tile. Accent: #00AEEF.
 */
export function TravelAppVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="travel-visual"
    >
      <PhoneFrame accentColor="#00AEEF">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] text-gray-500">Next trip</div>
              <div className="text-white text-sm font-bold">JFK → CDG</div>
            </div>
            <div className="w-9 h-9 rounded-xl bg-[#00AEEF]/20 flex items-center justify-center">
              <Plane className="w-4 h-4 text-[#00AEEF]" />
            </div>
          </div>

          {/* Boarding pass strip */}
          <div className="bg-[#1a1a1a] rounded-xl border border-white/5 overflow-hidden">
            <div className="px-3 py-2.5 bg-gradient-to-r from-[#00AEEF]/20 to-[#00AEEF]/5 border-b border-white/5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[8px] text-gray-400">Boarding</div>
                  <div className="text-white text-[11px] font-bold font-mono">
                    Tue · 7:45 PM
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[8px] text-gray-400">Gate</div>
                  <div className="text-[#00AEEF] text-[11px] font-bold font-mono">
                    B22
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[8px] text-gray-400">Seat</div>
                  <div className="text-white text-[11px] font-bold font-mono">
                    14A
                  </div>
                </div>
              </div>
            </div>
            <div className="px-3 py-2 flex items-center justify-between">
              <div>
                <div className="text-white text-[10px] font-bold">JFK</div>
                <div className="text-[8px] text-gray-500">New York</div>
              </div>
              <div className="flex-1 mx-2 relative h-px bg-white/10">
                <Plane className="w-2.5 h-2.5 text-[#00AEEF] absolute -top-1 left-1/2 -translate-x-1/2" />
              </div>
              <div className="text-right">
                <div className="text-white text-[10px] font-bold">CDG</div>
                <div className="text-[8px] text-gray-500">Paris</div>
              </div>
            </div>
          </div>

          {/* Trip timeline */}
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5">
            <div className="text-[9px] text-gray-500 mb-2">Itinerary</div>
            <div className="space-y-1.5">
              {[
                { icon: Plane, label: "Flight AF 023", time: "Tue 7:45 PM", color: "#00AEEF" },
                { icon: MapPin, label: "Le Bristol · Suite", time: "Wed 8:20 AM", color: "#10B981" },
                { icon: Compass, label: "Louvre · Skip-the-line", time: "Wed 2:00 PM", color: "#F59E0B" },
              ].map((row, i) => {
                const Icon = row.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -6 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.12 }}
                    className="flex items-center gap-2"
                  >
                    <div
                      className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${row.color}26` }}
                    >
                      <Icon className="w-3 h-3" style={{ color: row.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-white font-medium truncate">
                        {row.label}
                      </div>
                      <div className="text-[8px] text-gray-500">{row.time}</div>
                    </div>
                    <Bell className="w-2.5 h-2.5 text-gray-600" />
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Loyalty miles tile */}
          <div className="bg-[#00AEEF]/10 border border-[#00AEEF]/20 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[9px] text-[#00AEEF] font-medium">
                  Mileage · Platinum
                </div>
                <div className="text-white text-sm font-bold mt-0.5">
                  82,412 mi
                </div>
                <div className="text-[8px] text-gray-400">
                  +6,420 from this trip
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-[#00AEEF]/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-[#00AEEF]" />
              </div>
            </div>
          </div>
        </div>
      </PhoneFrame>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Wallet className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">
              Apple Wallet
            </div>
            <div className="text-gray-400 text-[8px]">
              Pass auto-updated
            </div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
            <Bell className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">
              Gate change
            </div>
            <div className="text-gray-400 text-[8px]">
              B14 → B22 · 12m ago
            </div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Insurance: browser chrome with policy summary, claim status timeline,
 * and premium trend chart. Accent: #10B981.
 */
export function InsuranceClaimVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="insurance-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#10B981]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  carrier.maxiom.app/claims/CLM-48217
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {/* Policy header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#10B981] flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">
                      Auto · POL-7142-A
                    </div>
                    <div className="text-[9px] text-gray-500">
                      M. Alvarez · 2023 Subaru Outback
                    </div>
                  </div>
                </div>
                <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
                  Active
                </div>
              </div>

              {/* Claim status timeline */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">
                    Claim CLM-48217 · FNOL
                  </div>
                  <div className="text-[8px] text-[#10B981]">3 of 4</div>
                </div>
                <div className="space-y-1.5">
                  {[
                    { label: "FNOL received", time: "Mon 9:14 AM", done: true },
                    { label: "Photos reviewed · AI triage", time: "Mon 9:18 AM", done: true },
                    { label: "Adjuster assigned · J. Chen", time: "Mon 10:02 AM", done: true },
                    { label: "Settlement issued", time: "Pending", done: false },
                  ].map((s, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -6 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.12 }}
                      className="flex items-center gap-2"
                    >
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          background: s.done ? "#10B98133" : "#1f1f1f",
                          border: s.done
                            ? "1px solid #10B98166"
                            : "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        {s.done ? (
                          <CheckCircle className="w-3 h-3 text-[#10B981]" />
                        ) : (
                          <Clock className="w-2.5 h-2.5 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-white font-medium truncate">
                          {s.label}
                        </div>
                        <div className="text-[8px] text-gray-500">{s.time}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Premium + risk row */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[8px] text-gray-500">
                      Premium · 12mo
                    </span>
                    <span className="text-[8px] text-[#10B981]">−3.2%</span>
                  </div>
                  <div className="flex items-end gap-1 h-8">
                    {[60, 65, 70, 64, 58, 62, 55, 50].map((h, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${h}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.6 + i * 0.05, duration: 0.4 }}
                        className="flex-1 rounded-sm bg-gradient-to-t from-[#10B981] to-[#10B981]/20"
                      />
                    ))}
                  </div>
                </div>
                <div className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5">
                  <div className="text-[8px] text-gray-500">Risk score</div>
                  <div className="text-white text-base font-bold mt-1">
                    A−
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {[1, 1, 1, 1, 0].map((on, i) => (
                      <div
                        key={i}
                        className="h-1 flex-1 rounded-full"
                        style={{
                          background: on ? "#10B981" : "rgba(255,255,255,0.08)",
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <FileText className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">
              Guidewire
            </div>
            <div className="text-gray-400 text-[8px]">
              ClaimCenter synced
            </div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <ScanLine className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">
              AI photo triage
            </div>
            <div className="text-gray-400 text-[8px]">
              97.4% accuracy
            </div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Cost Estimator: browser chrome with feature toggles, animated price range,
 * and breakdown chips. Accent: #F59E0B.
 */
export function CostEstimatorVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="cost-estimator-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#F59E0B]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  maxiom.app/estimate
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#F59E0B] flex items-center justify-center">
                    <Calculator className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">
                      App Cost Estimate
                    </div>
                    <div className="text-[9px] text-gray-500">
                      Step 4 of 6 · Features
                    </div>
                  </div>
                </div>
                <div className="text-[8px] text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/20 px-2 py-1 rounded-full font-mono">
                  ~78% done
                </div>
              </div>

              {/* Platform toggles */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="text-[9px] text-gray-500 mb-2">Platforms</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { label: "iOS", on: true },
                    { label: "Android", on: true },
                    { label: "Web", on: false },
                  ].map((p, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 4 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.08 }}
                      className="rounded-md px-2 py-1.5 flex items-center gap-1.5 border"
                      style={{
                        background: p.on ? "#F59E0B26" : "#0f0f0f",
                        borderColor: p.on
                          ? "#F59E0B66"
                          : "rgba(255,255,255,0.05)",
                      }}
                    >
                      <div
                        className="w-3 h-3 rounded-sm flex items-center justify-center"
                        style={{
                          background: p.on ? "#F59E0B" : "transparent",
                          border: p.on
                            ? "none"
                            : "1px solid rgba(255,255,255,0.15)",
                        }}
                      >
                        {p.on && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className="text-[9px] text-gray-200">
                        {p.label}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Feature checkboxes */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="text-[9px] text-gray-500 mb-2">
                  Core features
                </div>
                <div className="space-y-1.5">
                  {[
                    { label: "User accounts + SSO", on: true },
                    { label: "Payments (Stripe)", on: true },
                    { label: "Push notifications", on: true },
                    { label: "Offline-first sync", on: false },
                    { label: "AI / LLM features", on: true },
                  ].map((f, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -4 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + i * 0.07 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-sm flex items-center justify-center"
                          style={{
                            background: f.on ? "#F59E0B" : "transparent",
                            border: f.on
                              ? "none"
                              : "1px solid rgba(255,255,255,0.15)",
                          }}
                        >
                          {f.on && (
                            <CheckCircle className="w-2.5 h-2.5 text-white" />
                          )}
                        </div>
                        <span className="text-[10px] text-gray-300">
                          {f.label}
                        </span>
                      </div>
                      <span className="text-[8px] text-gray-500 font-mono">
                        {f.on ? "+$" + (8 + i * 2) + "K" : "—"}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Estimate output */}
              <div className="bg-gradient-to-r from-[#F59E0B]/15 to-[#F59E0B]/5 border border-[#F59E0B]/30 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[9px] text-[#F59E0B] font-medium">
                      Estimated build cost
                    </div>
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 1.0 }}
                      className="text-white text-lg font-bold mt-0.5 font-mono"
                    >
                      $72.4K – $138.9K
                    </motion.div>
                    <div className="text-[8px] text-gray-400">
                      Expected · $98,400 · 14 wk
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
            <Award className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">
              Engineer reviewed
            </div>
            <div className="text-gray-400 text-[8px]">
              Senior · since 2002
            </div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">
              Under 3 min
            </div>
            <div className="text-gray-400 text-[8px]">
              Real range · no spam
            </div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Cross-platform landing: dual-store release dashboard.
 * Accent: #00AEEF (mobile). Browser chrome, not a reused RN phone.
 */
export function CrossPlatformStoresVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="cross-platform-stores-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#00AEEF]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  harbor.ops/releases/2.1.0
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#00AEEF] flex items-center justify-center">
                    <Layers className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">
                      Harbor Ops
                    </div>
                    <div className="text-[9px] text-gray-500">
                      Shared RN · Sprint 11
                    </div>
                  </div>
                </div>
                <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
                  Both stores
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Shared code", val: "87%", pct: "+3 pts", color: "#00AEEF" },
                  { label: "iOS crash-free", val: "99.91%", pct: "+0.12%", color: "#10B981" },
                  { label: "Android crash-free", val: "99.86%", pct: "+0.08%", color: "#F59E0B" },
                ].map((m) => (
                  <div
                    key={m.label}
                    className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5"
                  >
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-1">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>
                      {m.pct}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Apple className="w-3 h-3 text-[#00AEEF]" />
                    <div className="text-[8px] uppercase tracking-widest text-gray-500">
                      TestFlight
                    </div>
                  </div>
                  <div className="text-white text-[11px] font-semibold">
                    2.1.0 (184)
                  </div>
                  <div className="text-[8px] text-[#10B981] mt-0.5">
                    Review in progress
                  </div>
                  <div className="w-full h-1.5 bg-[#0f0f0f] rounded-full mt-2 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "78%" }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4, duration: 0.8 }}
                      className="h-full rounded-full bg-[#00AEEF]"
                    />
                  </div>
                </div>
                <div className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Store className="w-3 h-3 text-[#10B981]" />
                    <div className="text-[8px] uppercase tracking-widest text-gray-500">
                      Play Internal
                    </div>
                  </div>
                  <div className="text-white text-[11px] font-semibold">
                    2.1.0 (184)
                  </div>
                  <div className="text-[8px] text-[#10B981] mt-0.5">
                    Staged 25% rollout
                  </div>
                  <div className="w-full h-1.5 bg-[#0f0f0f] rounded-full mt-2 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "25%" }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.55, duration: 0.8 }}
                      className="h-full rounded-full bg-[#10B981]"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Native vs shared</div>
                  <div className="text-[9px] text-[#00AEEF] font-medium">
                    BLE + camera in Swift/Kotlin
                  </div>
                </div>
                <div className="flex items-end gap-1 h-12">
                  {[62, 71, 68, 79, 74, 83, 81, 87].map((h, i) => {
                    const isLast = i === 7;
                    return (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${h}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.35 + i * 0.08, duration: 0.5 }}
                        className={`flex-1 rounded-sm ${
                          isLast ? "bg-[#00AEEF]" : "bg-[#00AEEF]/30"
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Apple className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">App Store</div>
            <div className="text-[#00AEEF] text-[8px]">Build 184 live</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Play Console</div>
            <div className="text-[#10B981] text-[8px]">25% staged</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Startup / product-app landing: sprint board + delivery KPIs.
 * Accent: #8B5CF6 (product / custom software). Browser chrome.
 */
export function StartupProductBoardVisual() {
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="startup-product-board-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#8B5CF6]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  northstar.app/sprint/14
                </span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#8B5CF6] flex items-center justify-center">
                    <Rocket className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">
                      Northstar
                    </div>
                    <div className="text-[9px] text-gray-500">
                      Sprint 14 · Seed demo Friday
                    </div>
                  </div>
                </div>
                <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
                  On track
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Velocity", val: "42", pct: "+6 pts", color: "#8B5CF6" },
                  { label: "Cycle Time", val: "2.4d", pct: "−0.6d", color: "#10B981" },
                  { label: "Activation", val: "27.4%", pct: "+4.1%", color: "#00AEEF" },
                ].map((m) => (
                  <div
                    key={m.label}
                    className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5"
                  >
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-1">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>
                      {m.pct}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    col: "Now",
                    cards: [
                      { title: "Invite codes", tag: "iOS" },
                      { title: "Stripe checkout", tag: "API" },
                    ],
                  },
                  {
                    col: "Next",
                    cards: [
                      { title: "Activation emails", tag: "Web" },
                      { title: "Waitlist analytics", tag: "Data" },
                    ],
                  },
                  {
                    col: "Shipped",
                    cards: [
                      { title: "Auth + magic link", tag: "Done" },
                      { title: "TestFlight 0.9", tag: "Done" },
                    ],
                  },
                ].map((col) => (
                  <div
                    key={col.col}
                    className="bg-[#1a1a1a] rounded-lg p-2 border border-white/5"
                  >
                    <div className="text-[8px] uppercase tracking-widest text-gray-500 mb-1.5">
                      {col.col}
                    </div>
                    <div className="space-y-1.5">
                      {col.cards.map((card) => (
                        <div
                          key={card.title}
                          className="bg-[#0f0f0f] rounded-md px-2 py-1.5 border border-white/5"
                        >
                          <div className="text-[9px] text-gray-200 leading-tight">
                            {card.title}
                          </div>
                          <div className="text-[7px] text-[#8B5CF6] mt-0.5">
                            {card.tag}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">PRs merged</div>
                  <div className="text-[9px] text-[#8B5CF6] font-medium">
                    18 this sprint
                  </div>
                </div>
                <div className="flex items-end gap-1 h-12">
                  {[8, 11, 9, 14, 12, 16, 13, 18].map((h, i) => {
                    const isLast = i === 7;
                    return (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${(h / 18) * 100}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.35 + i * 0.08, duration: 0.5 }}
                        className={`flex-1 rounded-sm ${
                          isLast ? "bg-[#8B5CF6]" : "bg-[#8B5CF6]/30"
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
            <Timer className="w-4 h-4 text-[#8B5CF6]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Cycle Time</div>
            <div className="text-[#8B5CF6] text-[8px]">2.4 days</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">TestFlight</div>
            <div className="text-[#10B981] text-[8px]">Build 0.9.4 live</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/* ------------------------------------------------------------------------ *
 * LocationMapVisual: config-driven, stylized interactive map for location
 * landing pages (DC, VA, MD, NYC, Boston, Austin, LA). Renders browser-style
 * chrome, an SVG map with grid + roads + animated pins + client connections,
 * a side panel with regional stats + recent engagements, and floating
 * compliance / flavor cards. All HTML/CSS: no stock map tiles.
 *
 * Config schema (see /data/locationMapConfigs.js):
 *   id, region, subtitle, searchPlaceholder, accentColor,
 *   roads?: [pathD], waterAreas?: [{ d, opacity? }],
 *   districts?: [{ x, y, text, anchor?, bold? }],
 *   pins: [{ x, y, type: "hq"|"client", label? }],
 *   stats: [{ value, label }] (exactly 4),
 *   recentClients: [string] (3–4),
 *   badges: [{ Icon, title, subtitle }] (exactly 2)
 * ------------------------------------------------------------------------ */

export function LocationMapVisual({ config }) {
  if (!config) return null;
  const accent = config.accentColor || "#00AEEF";
  const gridId = `grid-${config.id || "loc"}`;
  const hqPin = config.pins.find((p) => p.type === "hq");
  const clientPins = config.pins.filter((p) => p.type !== "hq");

  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid={`location-map-${config.id}`}
    >
      <div className="relative bg-[#0a0a0a] rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
        {/* Browser-style top bar with search */}
        <div className="h-9 bg-[#1a1a1a] border-b border-white/10 flex items-center px-3 gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          </div>
          <div className="flex-1 mx-3 h-5 bg-[#0a0a0a] rounded-md border border-white/5 flex items-center px-2 gap-1.5">
            <Search className="w-2.5 h-2.5 text-gray-500" />
            <span className="text-[9px] text-gray-500 truncate">
              {config.searchPlaceholder || `Search ${config.region}…`}
            </span>
          </div>
          <MapPin className="w-3.5 h-3.5" style={{ color: accent }} />
        </div>

        <div className="flex h-[340px]">
          {/* Map area (SVG) */}
          <div className="flex-1 relative bg-[#0f0f0f] border-r border-white/5">
            <svg viewBox="0 0 400 280" className="w-full h-full">
              <defs>
                <pattern
                  id={gridId}
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 20 0 L 0 0 0 20"
                    fill="none"
                    stroke="white"
                    strokeOpacity="0.04"
                    strokeWidth="0.5"
                  />
                </pattern>
                <radialGradient id={`glow-${config.id}`} cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor={accent} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={accent} stopOpacity="0" />
                </radialGradient>
              </defs>
              <rect width="400" height="280" fill={`url(#${gridId})`} />

              {/* Water bodies / parks: soft tinted areas */}
              {(config.waterAreas || []).map((a, i) => (
                <path
                  key={`water-${i}`}
                  d={a.d}
                  fill={accent}
                  fillOpacity={a.opacity || 0.06}
                />
              ))}

              {/* Roads / corridors */}
              {(config.roads || []).map((d, i) => (
                <path
                  key={`road-${i}`}
                  d={d}
                  stroke="white"
                  strokeOpacity="0.08"
                  strokeWidth="1.4"
                  fill="none"
                />
              ))}

              {/* Soft glow under HQ */}
              {hqPin && (
                <circle
                  cx={hqPin.x}
                  cy={hqPin.y}
                  r="60"
                  fill={`url(#glow-${config.id})`}
                />
              )}

              {/* Animated connection lines HQ → clients */}
              {hqPin &&
                clientPins.map((c, j) => (
                  <motion.line
                    key={`conn-${j}`}
                    x1={hqPin.x}
                    y1={hqPin.y}
                    x2={c.x}
                    y2={c.y}
                    stroke={accent}
                    strokeOpacity="0.35"
                    strokeWidth="1"
                    strokeDasharray="3 3"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 + j * 0.15, duration: 0.7 }}
                  />
                ))}

              {/* District labels */}
              {(config.districts || []).map((d, i) => (
                <motion.text
                  key={`district-${i}`}
                  x={d.x}
                  y={d.y}
                  fill="white"
                  fillOpacity={d.bold ? "0.7" : "0.4"}
                  fontSize={d.bold ? "9" : "8"}
                  fontWeight={d.bold ? "600" : "400"}
                  textAnchor={d.anchor || "start"}
                  letterSpacing="0.4"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.06 }}
                >
                  {d.text}
                </motion.text>
              ))}

              {/* Pins */}
              {config.pins.map((p, i) => {
                const isHq = p.type === "hq";
                const r = isHq ? 5.5 : 3.5;
                return (
                  <motion.g
                    key={`pin-${i}`}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: 0.4 + i * 0.08,
                      type: "spring",
                      stiffness: 220,
                    }}
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={r}
                      fill={accent}
                      fillOpacity="0.35"
                    >
                      <animate
                        attributeName="r"
                        values={`${r};${r + (isHq ? 10 : 8)};${r}`}
                        dur={isHq ? "2.4s" : "2.8s"}
                        repeatCount="indefinite"
                        begin={`${i * 0.35}s`}
                      />
                      <animate
                        attributeName="fill-opacity"
                        values="0.45;0;0.45"
                        dur={isHq ? "2.4s" : "2.8s"}
                        repeatCount="indefinite"
                        begin={`${i * 0.35}s`}
                      />
                    </circle>
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={isHq ? 4.5 : 3}
                      fill={accent}
                      stroke="#0f0f0f"
                      strokeWidth="1.5"
                    />
                    {isHq && (
                      <circle cx={p.x} cy={p.y} r="1.6" fill="white" />
                    )}
                    {p.label && (
                      <text
                        x={p.x + (isHq ? 10 : 7)}
                        y={p.y + 3}
                        fill="white"
                        fontSize={isHq ? "9" : "8"}
                        fontWeight={isHq ? "700" : "500"}
                      >
                        {p.label}
                      </text>
                    )}
                  </motion.g>
                );
              })}
            </svg>

            {/* Zoom controls */}
            <div className="absolute top-3 right-3 flex flex-col">
              <button
                className="w-6 h-6 bg-[#1a1a1a]/90 border border-white/10 rounded-t-md flex items-center justify-center text-white/60 text-sm"
                tabIndex={-1}
                aria-hidden="true"
              >
                +
              </button>
              <button
                className="w-6 h-6 bg-[#1a1a1a]/90 border-x border-b border-white/10 rounded-b-md flex items-center justify-center text-white/60 text-sm"
                tabIndex={-1}
                aria-hidden="true"
              >
                −
              </button>
            </div>

            {/* Legend */}
            <div className="absolute bottom-3 left-3 bg-[#1a1a1a]/85 backdrop-blur-sm border border-white/10 rounded-md px-2 py-1.5">
              <div className="flex items-center gap-3 text-[8px] text-gray-400">
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ background: accent }}
                  />
                  {config.legendHqLabel || "HQ"}
                </div>
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 rounded-full opacity-50"
                    style={{ background: accent }}
                  />
                  Client sites
                </div>
              </div>
            </div>

            {/* Scale bar */}
            <div className="absolute bottom-3 right-3 flex items-center gap-1 text-[8px] text-gray-500">
              <div className="w-10 h-0.5 bg-gray-500" />
              <span>5 mi</span>
            </div>
          </div>

          {/* Side panel */}
          <div className="w-48 bg-[#0a0a0a] p-3 space-y-3">
            <div>
              <div className="text-[9px] text-gray-500 uppercase tracking-wider">
                Region
              </div>
              <div className="text-white text-xs font-bold mt-0.5">
                {config.region}
              </div>
              <div className="text-gray-500 text-[9px] leading-tight">
                {config.subtitle}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              {config.stats.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 + i * 0.1 }}
                  className="bg-[#1a1a1a] rounded-md p-1.5 border border-white/5"
                >
                  <div
                    className="text-sm font-bold leading-none"
                    style={{ color: i === 0 ? accent : "white" }}
                  >
                    {s.value}
                  </div>
                  <div className="text-[8px] text-gray-500 leading-tight mt-1">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>

            <div>
              <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1.5">
                Recent engagements
              </div>
              <div className="space-y-1">
                {config.recentClients.map((c, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 6 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.9 + i * 0.1 }}
                    className="bg-[#1a1a1a] rounded-md px-2 py-1 border border-white/5 flex items-center gap-1.5"
                  >
                    <div
                      className="w-1 h-1 rounded-full flex-shrink-0"
                      style={{ background: accent }}
                    />
                    <span className="text-[9px] text-gray-300 truncate">
                      {c}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="pt-1 border-t border-white/5">
              <div className="flex items-center gap-1.5 text-[9px] text-gray-400">
                <Users className="w-3 h-3" style={{ color: accent }} />
                <span className="text-white font-medium">Local team online</span>
              </div>
              <div className="flex items-center gap-1 mt-1.5">
                {["#00AEEF", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"].map(
                  (c, i) => (
                    <div
                      key={i}
                      className="w-5 h-5 rounded-full border border-[#0a0a0a]"
                      style={{
                        background: `linear-gradient(135deg, ${c}, ${c}66)`,
                        marginLeft: i === 0 ? 0 : -6,
                      }}
                    />
                  )
                )}
                <span className="ml-1 text-[8px] text-gray-500">+12</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating context cards */}
      {(config.badges || []).map((b, i) => {
        const Icon = b.Icon;
        return (
          <FloatingCard
            key={i}
            className={i === 0 ? "-right-6 top-14" : "-left-6 bottom-16"}
            delay={1.2 + i * 0.3}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: `${accent}26` }}
              >
                <Icon className="w-4 h-4" style={{ color: accent }} />
              </div>
              <div>
                <div className="text-white text-[10px] font-medium">
                  {b.title}
                </div>
                <div className="text-gray-400 text-[8px]">{b.subtitle}</div>
              </div>
            </div>
          </FloatingCard>
        );
      })}
    </motion.div>
  );
}

/**
 * Take over a mobile app: repo and store handover, then a health check.
 * Accent: #00AEEF. Browser chrome. Decorative product data only.
 */
export function ProjectTakeoverVisual() {
  const rows = [
    { label: "Repo admin", detail: "northline/field", status: "Your org", color: "#10B981" },
    { label: "App Store", detail: "Admin invited", status: "Company", color: "#10B981" },
    { label: "Play account", detail: "Transfer started", status: "In progress", color: "#00AEEF" },
    { label: "Signing keys", detail: "Company vault", status: "Moved", color: "#10B981" },
    { label: "Health check", detail: "Build, crashes, last release", status: "Day 2", color: "#F59E0B" },
  ];

  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="project-takeover-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#00AEEF]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">handover.northline.app/health</span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#00AEEF] flex items-center justify-center">
                    <GitBranch className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">Northline Field</div>
                    <div className="text-[9px] text-gray-500">From the last agency · v2.4.0 live</div>
                  </div>
                </div>
                <div className="text-[8px] text-[#00AEEF] bg-[#00AEEF]/10 border border-[#00AEEF]/20 px-2 py-1 rounded-full">
                  Health check
                </div>
              </div>

              <div className="space-y-2">
                {rows.map((row, i) => (
                  <motion.div
                    key={row.label}
                    initial={{ opacity: 0, x: 12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.25 + i * 0.08, duration: 0.4 }}
                    className="flex items-center justify-between bg-[#1a1a1a] rounded-lg px-3 py-2 border border-white/5"
                  >
                    <div>
                      <div className="text-white text-[11px] font-medium">{row.label}</div>
                      <div className="text-[9px] text-gray-500">{row.detail}</div>
                    </div>
                    <span
                      className="text-[8px] font-medium px-2 py-1 rounded-full"
                      style={{ color: row.color, backgroundColor: `${row.color}22` }}
                    >
                      {row.status}
                    </span>
                  </motion.div>
                ))}
              </div>

              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[9px] text-gray-500">Next release · 2.4.1</div>
                  <div className="text-[9px] text-[#00AEEF] font-medium">Queued after the check</div>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "68%" }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                    className="h-full rounded-full bg-[#00AEEF]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="right-2 top-16" delay={1.1}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Store className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Accounts</div>
            <div className="text-[#10B981] text-[8px]">In your name</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="left-2 bottom-10" delay={1.4}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Crash-free</div>
            <div className="text-gray-400 text-[8px]">97.4% · last 7 days</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/**
 * Mid-page section mockup for the Security Audits page.
 * Pairs with copy about the preliminary report: risk posture, findings by
 * severity, and prioritized, evidence-backed findings with fixes.
 */
export function SecurityFindingsDashboardVisual() {
  const accent = "#10B981";
  const findings = [
    {
      icon: AlertCircle,
      sev: "High",
      color: "#EF4444",
      title: "Missing idempotency on payment capture",
      meta: "POST /v1/charges · evidence + fix",
    },
    {
      icon: AlertCircle,
      sev: "Medium",
      color: "#F59E0B",
      title: "Broad role grants on admin API",
      meta: "auth/roles.ts · evidence + fix",
    },
    {
      icon: CheckCircle,
      sev: "Low",
      color: "#00AEEF",
      title: "Verbose error messages in logs",
      meta: "logger.ts · evidence + fix",
    },
  ];
  return (
    <motion.div
      {...sectionMockupBase}
      className="hidden lg:block relative"
      data-testid="security-findings-visual"
    >
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 rounded-3xl blur-2xl" style={{ background: `${accent}10` }} />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">audit.maxiom.app/acme-payments/report</span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {/* Report header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: accent }}>
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">Preliminary Report</div>
                    <div className="text-[9px] text-gray-500">Tier 3 · 15 findings</div>
                  </div>
                </div>
                <div className="text-[8px] px-2 py-1 rounded-full border" style={{ color: accent, backgroundColor: `${accent}15`, borderColor: `${accent}30` }}>
                  Delivered
                </div>
              </div>

              {/* Risk posture */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3" style={{ color: accent }} />
                    <span className="text-[9px] text-gray-500">Risk posture</span>
                  </div>
                  <span className="text-[9px] font-medium text-white">Moderate · improving</span>
                </div>
                <div className="flex items-end gap-1 h-2">
                  {[
                    { w: "8%", c: "#10B981" },
                    { w: "34%", c: "#00AEEF" },
                    { w: "40%", c: "#F59E0B" },
                    { w: "18%", c: "#EF4444" },
                  ].map((s, i) => (
                    <motion.div
                      key={i}
                      initial={{ width: 0 }}
                      whileInView={{ width: s.w }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.12, duration: 0.5 }}
                      className="h-full rounded-sm"
                      style={{ backgroundColor: s.c }}
                    />
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2 text-[8px] text-gray-600">
                  <span>Critical 0</span>
                  <span>High 2</span>
                  <span>Med 5</span>
                  <span>Low 8</span>
                </div>
              </div>

              {/* Prioritized findings */}
              <div className="space-y-2">
                {findings.map((f, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.15, duration: 0.4 }}
                    className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5 flex items-start gap-2"
                  >
                    <f.icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: f.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-gray-200 font-medium truncate">{f.title}</span>
                        <span className="text-[8px] px-1.5 py-0.5 rounded flex-shrink-0" style={{ color: f.color, backgroundColor: `${f.color}15` }}>
                          {f.sev}
                        </span>
                      </div>
                      <div className="text-[8px] text-gray-500 mt-0.5 truncate">{f.meta}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">0 criticals</div>
            <div className="text-[#10B981] text-[8px]">Before sign-off</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Search className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Evidence-backed</div>
            <div className="text-gray-400 text-[8px]">Every finding</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}
